import React, { useState, useRef, useEffect } from 'react';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import './DomainFilter.css';

interface DomainFilterProps {
  configs: any[];
  selectedDomains: string[];
  onDomainsChange: (domains: string[]) => void;
  itemType?: 'frameworks' | 'rules';
}

interface DomainInfo {
  key?: string;
  display_name?: string;
  label?: string;
  description?: string;
  usage_count?: number;
  custom?: boolean;
  has_rules?: boolean;
  framework_count?: number;
}


const DomainFilter: React.FC<DomainFilterProps> = ({ configs, selectedDomains, onDomainsChange, itemType = 'frameworks' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [domainData, setDomainData] = useState<Record<string, DomainInfo>>({});
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch domain data from API
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        setLoading(true);
        const response = await knowledgeBaseApi.getAvailableDomains();
        if (response?.domains) {
          setDomainData(response.domains);
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error);
        // Fallback to extracting from configs if API fails
        const extractedDomains: Record<string, DomainInfo> = {};
        configs.forEach(config => {
          const domains = config.data?.domains || config.config_data?.domains || [];
          domains.forEach((domain: string) => {
            if (!extractedDomains[domain]) {
              extractedDomains[domain] = {
                label: domain.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                description: 'Domain',
                usage_count: 0
              };
            }
            extractedDomains[domain].usage_count = (extractedDomains[domain].usage_count || 0) + 1;
          });
        });
        setDomainData(extractedDomains);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDomains();
  }, [configs]);

  // Get sorted list of domains that actually exist in the configs
  const availableDomains = Object.keys(domainData)
    .filter(domain => domainData[domain] && (domainData[domain].usage_count || 0) > 0)
    .sort();

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDomainToggle = (domain: string) => {
    if (selectedDomains.includes(domain)) {
      onDomainsChange(selectedDomains.filter(d => d !== domain));
    } else {
      onDomainsChange([...selectedDomains, domain]);
    }
  };

  const handleSelectAll = () => {
    if (selectedDomains.length === availableDomains.length) {
      onDomainsChange([]);
    } else {
      onDomainsChange(availableDomains);
    }
  };

  const getButtonLabel = () => {
    if (selectedDomains.length === 0) {
      return 'All Domains';
    } else if (selectedDomains.length === 1) {
      return domainData[selectedDomains[0]]?.label || selectedDomains[0];
    } else if (selectedDomains.length === availableDomains.length) {
      return 'All Domains';
    } else {
      return `${selectedDomains.length} Domains`;
    }
  };

  const totalFrameworks = configs.length;
  const filteredCount = selectedDomains.length === 0 
    ? totalFrameworks 
    : configs.filter(c => {
        const domains = c.data?.domains || c.config_data?.domains || [];
        return domains.some((d: string) => selectedDomains.includes(d));
      }).length;

  if (loading) {
    return (
      <div className="domain-filter">
        <button className="domain-filter-button" disabled>
          <span className="domain-filter-label">Loading domains...</span>
        </button>
      </div>
    );
  }

  return (
    <div className="domain-filter" ref={dropdownRef}>
      <button 
        className="domain-filter-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="domain-filter-label">
          {getButtonLabel()}
        </span>
        <span className="domain-filter-arrow">
          {isOpen ? '▴' : '▾'}
        </span>
      </button>

      {isOpen && (
        <div className="domain-filter-dropdown">
          <div className="domain-filter-header">
            <button 
              className="domain-filter-select-all"
              onClick={handleSelectAll}
            >
              {selectedDomains.length === availableDomains.length ? 'Clear All' : 'Select All'}
            </button>
            <span className="domain-filter-count">
              {filteredCount} of {totalFrameworks} {itemType}
            </span>
          </div>
          
          <div className="domain-filter-options">
            {availableDomains.map(domain => (
              <label 
                key={domain}
                className="domain-filter-option"
              >
                <input
                  type="checkbox"
                  checked={selectedDomains.includes(domain)}
                  onChange={() => handleDomainToggle(domain)}
                />
                <span className="domain-filter-option-label">
                  {domainData[domain]?.label || domain}
                  {domainData[domain]?.custom && <span className="custom-badge">Custom</span>}
                </span>
                <span className="domain-filter-option-count">
                  {domainData[domain]?.usage_count || 0}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainFilter;